// Step 4 · Időzítés: now, or one wall clock that every country reads on its
// own clock. Every row shows the start on the reader's clock and the country's
// in brackets; UTC is not on the screen — it is what the API stores, not what
// a marketer schedules by.
//
// Nothing is sent from here. The choice is kept in the browser (per campaign)
// until the review step's button hands it to `POST /schedule`.

import type { ColumnDef } from "@tanstack/react-table";
import type { GroupCountryDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { Callout } from "@/components/emc/states";
import { cn } from "@/lib/utils";
import type { Editor } from "@/pages/campaigns/editor/context";
import {
  hourOf,
  insideWindow,
  isLocal,
  localMomentFor,
  nowIn,
  windowText,
} from "@/pages/campaigns/model";
import { wallToMoment, withCountryClock } from "@/components/emc/time";
import type { TimingDraft } from "@/pages/campaigns/model";
import { StepActions } from "@/pages/campaigns/parts";
import { Country } from "@/components/emc/Flag";

const MODES: { value: TimingDraft["mode"]; title: string; text: string }[] = [
  {
    value: "now",
    title: "Most",
    text: "A megerősítés után minden ország azonnal indul. Ahol épp zárva a küldési ablak, ott a nyitásakor.",
  },
  {
    value: "later",
    title: "Ütemezve",
    text: "Egy dátum és időpont, amit minden ország a saját helyi ideje szerint ért.",
  },
];

export function TimingStep({ editor }: { editor: Editor }) {
  const { timing, setTiming, summary, identityOf } = editor;
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";
  const wallClock = `${timing.date}T${timing.time}`;
  const complete = timing.mode === "now" || isLocal(wallClock);

  const columns: ColumnDef<GroupCountryDetail>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => <Country code={row.original.country} />,
    },
    {
      // The zone and the window ride under the moment: three columns fit the
      // step column, five made the override field scroll out of sight.
      id: "local",
      header: "Indulás",
      cell: ({ row }) => {
        const country = row.original.country;
        const identity = identityOf(country);
        const moment = localMomentFor(timing, country, zoneOf(country));
        const past = timing.mode === "later" && isLocal(moment) && moment <= nowIn(zoneOf(country));
        const hour = hourOf(moment);
        const outside =
          identity !== null && hour !== null
            ? !insideWindow(hour, identity.send_window_start_hour, identity.send_window_end_hour)
            : false;
        return (
          <span className="flex flex-col">
            <span className={cn("data-mono", past && "text-danger")}>
              {timing.mode === "now"
                ? "most"
                : complete
                  ? withCountryClock(wallToMoment(moment, zoneOf(country)), zoneOf(country))
                  : "—"}
            </span>
            {identity !== null && (
              <span className="text-label text-muted-foreground">
                küldési ablak {windowText(identity, timing.mode === "later" ? moment.slice(0, 10) : undefined)}
              </span>
            )}
            {past && <span className="text-label text-danger">már elmúlt</span>}
            {!past && outside && identity !== null && (
              <span className="text-label text-warn">az ablakon kívül — az ablak nyitásakor indul</span>
            )}
          </span>
        );
      },
      meta: { wrap: true },
    },
    {
      id: "override",
      header: "Eltérő időpont",
      cell: ({ row }) => {
        const country = row.original.country;
        if (timing.mode !== "later") return <span className="text-muted-foreground">—</span>;
        return (
          <span className="flex items-center gap-2">
            <Input
              type="datetime-local"
              aria-label={`${country} eltérő indulása, helyi idő`}
              className="w-56"
              value={timing.overrides[country] ?? ""}
              disabled={!editor.editable && editor.summary.state !== "review"}
              onChange={(event) => {
                const overrides = { ...timing.overrides };
                if (event.target.value === "") delete overrides[country];
                else overrides[country] = event.target.value.slice(0, 16);
                setTiming({ ...timing, overrides });
              }}
            />
            {timing.overrides[country] && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  const overrides = { ...timing.overrides };
                  delete overrides[country];
                  setTiming({ ...timing, overrides });
                }}
              >
                Törlés
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Mikor menjen ki?</CardTitle>
          <CardDescription>
            Az időpontot minden ország a saját órája szerint érti: a „09:00” Budapesten 09:00,
            Bukarestben is 09:00 — ami egy órával korábbi pillanat.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <fieldset className="m-0 grid gap-3 border-0 p-0 md:grid-cols-2">
            <legend className="sr-only">Időzítés</legend>
            {MODES.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-card border border-line bg-surface p-4 hover:bg-surface-2",
                  timing.mode === option.value && "border-primary bg-primary-soft hover:bg-primary-soft",
                )}
              >
                <input
                  type="radio"
                  name="timing-mode"
                  checked={timing.mode === option.value}
                  onChange={() => setTiming({ ...timing, mode: option.value })}
                  className="mt-1 size-4 shrink-0 accent-primary"
                />
                <span className="flex flex-col gap-1">
                  <span className="font-medium">{option.title}</span>
                  <span className="text-muted-foreground">{option.text}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {timing.mode === "later" && (
            <div className="flex flex-wrap gap-4">
              <Field label="Dátum" htmlFor="timing-date">
                <Input
                  id="timing-date"
                  type="date"
                  className="w-48"
                  value={timing.date}
                  onChange={(event) => setTiming({ ...timing, date: event.target.value })}
                />
              </Field>
              <Field label="Időpont, helyi idő szerint" htmlFor="timing-time">
                <Input
                  id="timing-time"
                  type="time"
                  className="w-36"
                  value={timing.time}
                  onChange={(event) => setTiming({ ...timing, time: event.target.value.slice(0, 5) })}
                />
              </Field>
            </div>
          )}

          <DataTable
            label="Indulás országonként"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={columns}
            data={summary.countries}
            getRowId={(row) => row.country}
            empty={<p className="px-4 py-6 text-center text-muted-foreground">Előbb válassz országokat.</p>}
          />

          <Callout>
            A napi warmup-keret poolonként érvényes: ami az indulás napján nem fér bele, a
            következő napokon megy ki. A keretet a kód tartja, nem az időzítés.
          </Callout>
        </CardContent>
      </Card>

      <StepActions
        onBack={() => editor.go("tartalom")}
        next={{
          label: "Tovább az áttekintéshez",
          onClick: () => editor.go("attekintes"),
          disabled: !complete,
          reason: complete ? null : "Adj meg dátumot és időpontot, vagy válaszd a »Most« lehetőséget.",
        }}
      />
    </div>
  );
}
